import { useCallback, useState, ReactNode, createContext } from 'react';
import { Farm } from 'app/agriculture/contexts/RequiredStartupProviders/FarmContext/farmSchema';
import { useFlyToFarm } from 'app/agriculture/hooks/useFlyTo/useFlyToFarm';
import { useCloudfrontCookies } from 'app/agriculture/contexts/RequiredStartupProviders/FarmContext/useCloudfrontCookies';
import { useNavigate, useParams } from 'react-router-dom';
import { useFarms } from 'app/agriculture/hooks/useFarms/useFarms';

interface FarmContext {
    farm: Farm;
    farms: Farm[];
    setFarm: (id: number) => void;
}

export const FarmContext = createContext<FarmContext>({} as FarmContext);

export function FarmContextProvider({ children }: { children: ReactNode }) {
    const farms = useFarms();
    const { flyToFarm } = useFlyToFarm();
    const { farmId } = useParams();
    const navigate = useNavigate();

    const [selectedFarmId, setSelectedFarmId] = useState<number>(() => {
        if (farmId == null || isNaN(Number(farmId))) {
            throw new Error('FARM NOT FOUND');
        }

        const initialFarm = findFarm({ farms, farmId: Number(farmId) });

        return initialFarm.id;
    });

    const selectedFarm = findFarm({ farms, farmId: selectedFarmId });

    useCloudfrontCookies(selectedFarm.id);

    const setFarm = useCallback(
        (newFarmId: number) => {
            setSelectedFarmId(newFarmId);
            const newFarm = findFarm({ farms, farmId: newFarmId });
            flyToFarm(newFarm);

            navigate(`/${newFarm.id}`);
        },
        [farms, flyToFarm, navigate],
    );

    return (
        <FarmContext.Provider value={{ farm: selectedFarm, farms, setFarm }}>
            {children}
        </FarmContext.Provider>
    );
}

function findFarm({ farmId, farms }: { farmId: number; farms: Farm[] }): Farm {
    const [fallbackFarm] = farms;
    if (!fallbackFarm) throw new Error('NO FARMS');

    const matchedFarm = farms.find((farm) => farm.id === farmId);

    if (!matchedFarm) {
        console.error(`Farm with id "${farmId}" not found in farms`);
        throw new Error('FARM NOT FOUND');
    }

    return matchedFarm;
}
